import React, { lazy } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import ErrorPage from "../components/ErrorPage";
import AuthLayout from "../layouts/AuthLayout";
import MainLayout from "../layouts/MainLayout";
import { useAuth } from "contexts/AuthContext";

// Lazy Loaded Pages
const Login = lazy(() => import("../pages/auth/login"));
const Dashboard = lazy(() => import("../pages/dashboard"));
const IndexPage = lazy(() => import("../pages/index"));
const SetupPassword = lazy(() => import("../pages/auth/setup-password"));
const ForgotPassword = lazy(() => import("../pages/auth/forgot-password"));
const ResetPassword = lazy(() => import("../pages/auth/reset-password"));
const Tutor = lazy(() => import("../pages/tutor"));
const Students = lazy(() => import("../pages/students"));
const Courses = lazy(() => import("../pages/courses"));
const CouponManagement = lazy(() => import("../pages/coupon-management"));
const AdminPermissions = lazy(() => import("../pages/admin-permissions"));
const Transactions = lazy(() => import("../pages/transactions"));
const Settings = lazy(() => import("../pages/settings/index"));
const Account = lazy(() => import("../pages/settings/account"));
const Notification = lazy(() => import("../pages/settings/notifications"));
const Security = lazy(() => import("../pages/settings/security"));
const Pricing = lazy(() => import("../pages/pricing"));
const Timetable = lazy(() => import("../pages/timetable"));

const ProtectedRoute = () => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }
  return <Outlet />;
};

const GuestRoute = () => {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
};

const router = createBrowserRouter([
  // Guest-only routes (redirect to /dashboard if already logged in)
  {
    element: <GuestRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/auth",
        element: <AuthLayout />,
        children: [
          { path: "login", element: <Login /> },
          { path: "setup-password", element: <SetupPassword /> },
        ],
      },
    ],
  },
  // Password reset — reachable whether or not the user is logged in, so an
  // admin arriving from an email link with an active session isn't bounced
  // into the dashboard. The session is only cleared once they actually reset.
  {
    path: "/auth",
    element: <AuthLayout />,
    errorElement: <ErrorPage />,
    children: [
      { path: "forgot-password", element: <ForgotPassword /> },
      { path: "reset-password", element: <ResetPassword /> },
    ],
  },
  // Protected routes (redirect to /auth/login if not logged in)
  {
    element: <ProtectedRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/",
        element: <MainLayout />,
        children: [
          { index: true, element: <IndexPage /> },
          { path: "dashboard", element: <Dashboard /> },
          { path: "tutors", element: <Tutor /> },
          { path: "students", element: <Students /> },
          { path: "courses", element: <Courses /> },
          { path: "transactions", element: <Transactions /> },
          { path: "pricing", element: <Pricing /> },
          { path: "timetable", element: <Timetable /> },
          { path: "coupon-management", element: <CouponManagement /> },
          { path: "admin-permissions", element: <AdminPermissions /> },
          {
            path: "settings",
            element: <Settings />,
            children: [
              { path: "account", element: <Account /> },
              { path: "notification", element: <Notification /> },
              { path: "security", element: <Security /> },
              { index: true, element: <Account /> },
            ],
          },
        ],
      },
    ],
  },
]);

export { router };
